// AdminLoginPage.js - Login page for agency and super admins
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';

const AdminLoginPage = ({ title = "Admin Portal", type = "agency", agencyCode }) => {
  const navigate = useNavigate();
  const { login: authLogin, checkAuth, user } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authLogin({
        email: formData.email,
        password: formData.password,
        adminType: type,
        agencyCode: agencyCode
      });
      
      // Get user data from response - handle the nested response structure
      // Backend returns: { success, data: { token, user }, message, timestamp }
      const loginUser = response.data?.data?.user || response.data?.user || response.user || response.data;
      console.log('Login response:', { response, user: loginUser, fullResponseData: response.data });
      
      // Fetch full profile to get status field
      await checkAuth();
      
      // Wait for state update, then use user from context
      setTimeout(() => {
        const currentUser = user || loginUser;
        console.log('Navigating based on user role:', currentUser.role, 'status:', currentUser.status);
        
        // Check for commission-only agents first
        if (currentUser.role === 'agent' && currentUser.status === 'commission_only') {
          console.log('Commission-only agent detected, redirecting to commissions-only page');
          navigate('/agent/commissions-only');
          return;
        }
        
        // Navigate based on user role
        if (currentUser.role === 'super_admin') {
          navigate('/admin/dashboard');
        } else if (currentUser.role === 'agency_admin') {
          // Redirect agency admins directly to /admin/dashboard (not legacy /agency/dashboard)
          navigate('/admin/dashboard');
        } else if (currentUser.role === 'agents_manager') {
          // Sub-admin role for agent management
          navigate('/admin/dashboard');
        } else if (currentUser.role === 'commissions_manager') {
          // Sub-admin role for commission management
          navigate('/admin/dashboard');
        } else if (currentUser.role === 'agent') {
          navigate('/agent/dashboard');
        } else {
          // If somehow a non-admin logs in here, send them to agent dashboard
          navigate('/agent/dashboard');
        }
      }, 200);
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const isSuper = type === 'super';

  return (
    <div className={`min-h-screen flex items-center justify-center bg-gradient-to-br ${isSuper ? 'from-red-50 to-red-100' : 'from-gray-50 to-gray-100'} py-12 px-4 sm:px-6 lg:px-8`}>
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="text-center">
            <div className={`mx-auto h-16 w-16 ${isSuper ? 'bg-red-600' : 'bg-gray-800'} rounded-full flex items-center justify-center mb-4`}>
              <svg className="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900">
              {title}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Secure administrator access only
            </p>
          </div>
        </div>

        <div className="bg-white py-8 px-4 shadow-xl rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Admin Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  className="appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    {showPassword ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    )}
                  </svg>
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${isSuper ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' : 'bg-gray-800 hover:bg-gray-900 focus:ring-gray-500'} focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50`}
              >
                {loading ? 'Authenticating...' : 'Sign in'}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/admin/forgot-password')}
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                Forgot your password?
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="text-center text-sm text-gray-600">
              <p className="mb-2">⚠️ Authorized personnel only</p>
              {isSuper && (
                <p className="text-xs text-red-600">
                  This is a super administrator portal with full system access.
                  All actions are logged and monitored.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="text-center">
          <a href="/" className="text-sm text-gray-600 hover:text-gray-900">
            ← Back to home
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
